import { z } from 'zod'
import { api, apiRequest } from '../api/client'
import {
  WorkflowListResponseSchema,
  WorkflowDetailSchema,
  WorkflowSummarySchema,
  VersionListResponseSchema,
  VersionDefinitionResponseSchema,
  RunnableListResponseSchema,
  SaveDraftResponseSchema,
  PublishResponseSchema,
  UserListResponseSchema,
  LookupDatasetResponseSchema,
} from '@shared/schema/api'
import type { FlowDefinition } from '@shared/schema/flow'
import type { FlowTemplate } from '@shared/seed/foodVendor'

const WorkflowWrapperSchema = z.object({ workflow: WorkflowSummarySchema })
const AssignmentsResponseSchema = z.object({ userIds: z.array(z.string()) })
const RestoreResponseSchema = z.object({ rev: z.number(), updatedAt: z.number() })

export const workflowApi = {
  list: () => api.get('/api/workflows', WorkflowListResponseSchema),

  create: (name: string, template: FlowTemplate) =>
    api.post('/api/workflows', { name, template }, WorkflowWrapperSchema),

  detail: (id: string, signal?: AbortSignal) =>
    api.get(`/api/workflows/${id}`, WorkflowDetailSchema, signal),

  saveDraft: (id: string, definition: FlowDefinition, baseRev: number) =>
    api.put(`/api/workflows/${id}/draft`, { definition, baseRev }, SaveDraftResponseSchema),

  patch: (id: string, patch: { name?: string; status?: 'active' | 'archived' }) =>
    api.patch(`/api/workflows/${id}`, patch, WorkflowWrapperSchema),

  publish: (id: string, label?: string) =>
    api.post(`/api/workflows/${id}/publish`, label ? { label } : {}, PublishResponseSchema),

  versions: (id: string) => api.get(`/api/workflows/${id}/versions`, VersionListResponseSchema),

  restore: (id: string, versionId: string) =>
    api.post(`/api/workflows/${id}/restore`, { versionId }, RestoreResponseSchema),

  setAssignments: (id: string, userIds: string[]) =>
    api.put(`/api/workflows/${id}/assignments`, { userIds }, AssignmentsResponseSchema),

  users: () => api.get('/api/users', UserListResponseSchema),

  runnable: () => api.get('/api/run', RunnableListResponseSchema),

  runDefinition: (workflowId: string, signal?: AbortSignal) =>
    api.get(`/api/run/${workflowId}/definition`, VersionDefinitionResponseSchema, signal),

  remove: (id: string) => apiRequest(`/api/workflows/${id}`, { method: 'DELETE' }),

  getLookup: (id: string) => api.get(`/api/workflows/${id}/lookup`, LookupDatasetResponseSchema),

  /** ส่งข้อมูลอ้างอิงหนึ่งชุด — replace=true คือชุดแรกของไฟล์ (ล้างของเดิม), false คือต่อท้าย */
  uploadLookupChunk: (
    id: string,
    input: {
      fileName: string
      matchColumns: string[]
      columns: string[]
      rows: Record<string, string>[]
      replace: boolean
    },
  ) => api.post(`/api/workflows/${id}/lookup`, input, LookupDatasetResponseSchema),

  deleteLookup: (id: string) => apiRequest(`/api/workflows/${id}/lookup`, { method: 'DELETE' }),
}
